import { useEffect, useRef, useState } from "react";
import {
  ArrowDownTrayIcon,
  ComputerDesktopIcon,
} from "@heroicons/react/24/outline";
import * as Sentry from "@sentry/react";
import { supabase } from "../../lib/supabase";

interface InstallerFile {
  name: string;
  os: string;
  icon: string;
  filename: string;
}

const installers: InstallerFile[] = [
  {
    name: "Windows",
    os: "Windows 10+",
    icon: "W",
    filename: "Bookburrow.msi",
  },
  {
    name: "Linux",
    os: "Ubuntu 22.04+ / Debian 12+",
    icon: "L",
    filename: "Bookburrow.AppImage",
  },
];

function detectOS(): string {
  const ua = navigator.userAgent;
  if (ua.includes("Win")) return "Windows";
  if (ua.includes("Linux")) return "Linux";
  return "";
}

export function Downloads() {
  const detectedOS = detectOS();
  const [downloading, setDownloading] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const downloadTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    return () => clearTimeout(downloadTimerRef.current);
  }, []);

  async function handleDownload(installer: InstallerFile) {
    clearTimeout(downloadTimerRef.current);
    setDownloading(installer.name);
    setDownloadError(null);
    try {
      const { data, error } = await supabase.functions.invoke(
        "get-download-url",
        { body: { filename: installer.filename } },
      );

      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      }
      downloadTimerRef.current = setTimeout(() => setDownloading(null), 2000);
    } catch (err) {
      Sentry.captureException(err);
      setDownloadError(
        err instanceof Error ? err.message : "Download failed. Please try again.",
      );
      setDownloading(null);
    }
  }

  return (
    <div className="workspace-page p-6 sm:p-8 lg:p-10">
      <div>
        <h1 className="font-display text-2xl font-semibold text-forest-950">
          Downloads
        </h1>
        <p className="mt-1 text-sm text-bark-600">
          Download the latest version of Bookburrow for your computer.
        </p>
      </div>

      {downloadError && (
        <div className="mt-6 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
          {downloadError}
        </div>
      )}

      <div className="mt-8 grid gap-4 sm:grid-cols-2">
        {installers.map((installer) => (
          <button
            key={installer.name}
            onClick={() => handleDownload(installer)}
            disabled={downloading === installer.name}
            className={`group rounded-xl border p-6 text-left shadow-sm transition-all hover:border-forest-200 hover:shadow-md ${
              installer.name === detectedOS
                ? "border-forest-300 ring-1 ring-forest-200"
                : "border-cream-300/60"
            }`}
          >
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-forest-50 font-display text-lg font-bold text-forest-700 transition-colors group-hover:bg-forest-100">
                {installer.icon}
              </div>
              <div>
                <p className="font-semibold text-forest-900">
                  {installer.name}
                </p>
                <p className="text-xs text-bark-600/60">
                  {installer.os}
                  {installer.name === detectedOS && (
                    <span className="ml-2 rounded-full bg-forest-100 px-2 py-0.5 text-xs font-medium text-forest-700">
                      Recommended
                    </span>
                  )}
                </p>
              </div>
            </div>
            <div className="mt-5 flex items-center gap-2 text-sm font-medium text-forest-600">
              <ArrowDownTrayIcon className="h-4 w-4" />
              {downloading === installer.name ? "Preparing..." : "Download"}
            </div>
          </button>
        ))}
      </div>

      <div className="paper-card mt-10 rounded-xl border border-cream-300/60 bg-white p-6 shadow-sm">
        <div className="flex items-start gap-4">
          <ComputerDesktopIcon className="mt-0.5 h-6 w-6 flex-shrink-0 text-forest-400" />
          <div>
            <h2 className="font-display text-base font-semibold text-forest-900">
              System requirements
            </h2>
            <ul className="mt-3 space-y-1.5 text-sm text-bark-600">
              <li>
                <span className="font-medium text-bark-700">Windows:</span>{" "}
                Windows 10 or later, 64-bit
              </li>
              <li>
                <span className="font-medium text-bark-700">Linux:</span>{" "}
                Ubuntu 22.04+, Debian 12+, or equivalent with WebKitGTK
              </li>
              <li>
                <span className="font-medium text-bark-700">Storage:</span>{" "}
                100 MB free disk space
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
